import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, ArrowLeft, FileText, CheckCircle2, Calendar, ChevronDown, ChevronUp, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";
import DiscussionForum from "@/components/DiscussionForum";
import ActivityViewer from "@/components/ActivityViewer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Truncated Text Component with Read More functionality
function TruncatedText({ content, maxLength = 100 }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!content || content.length <= maxLength) {
    return <div className="text-[#344E41] leading-relaxed whitespace-pre-wrap text-sm sm:text-base">{content}</div>;
  }

  const truncatedContent = content.slice(0, maxLength).trim() + '...';

  return (
    <div>
      <div className="text-[#344E41] leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
        {isExpanded ? content : truncatedContent}
      </div>
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="read-more-button"
        className="mt-3 text-[#3A5A40] hover:bg-[#E9EDC9] font-medium text-sm px-0 hover:underline"
      >
        {isExpanded ? (
          <>
            <ChevronUp className="w-4 h-4 mr-1" />
            Read Less
          </>
        ) : (
          <>
            <ChevronDown className="w-4 h-4 mr-1" />
            Read More
          </>
        )}
      </Button>
    </div>
  );
}

// Missing Requirements Modal Component
function MissingRequirementsModal({ isOpen, onClose, requirements, onGoToLesson }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <Card className="relative z-10 w-full max-w-lg bg-[#FEFAE0] border-[#CCD5AE] shadow-xl rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[#D4A373]">
            <AlertCircle className="w-6 h-6" />
            <h3 className="text-xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Complete Required Activities
            </h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-[#588157] hover:bg-[#E9EDC9]">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <p className="text-[#588157] mb-4">
          Before submitting, please complete the following activities:
        </p>
        
        <div className="space-y-3">
          {requirements.map((req, index) => (
            <div 
              key={index} 
              className="p-3 bg-[#FAEDCD] rounded-lg border border-[#CCD5AE]/50 flex items-start gap-3"
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                req.type === 'activity' ? 'bg-[#D4A373]' : 
                req.type === 'discussion' ? 'bg-[#588157]' : 'bg-[#3A5A40]'
              }`}>
                {req.lesson_index >= 0 ? req.lesson_index + 1 : '!'}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#344E41]">{req.message}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  req.type === 'activity' ? 'bg-[#D4A373]/20 text-[#D4A373]' : 
                  req.type === 'discussion' ? 'bg-[#588157]/20 text-[#588157]' : 'bg-[#3A5A40]/20 text-[#3A5A40]'
                }`}>
                  {req.type === 'activity' ? 'Daily Activity' : 
                   req.type === 'discussion' ? 'Activity Discussion' : 'Course Discussion'}
                </span>
              </div>
              {req.lesson_index >= 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onGoToLesson(req.lesson_index, req.type)}
                  className="text-xs border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white"
                >
                  Go
                </Button>
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-6 flex justify-end">
          <Button onClick={onClose} className="bg-[#3A5A40] text-white hover:bg-[#344E41]">
            Got it
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function CourseViewer({ user }) {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(null); // null = loading, true/false = checked
  const [currentLesson, setCurrentLesson] = useState(0);
  const [allActivities, setAllActivities] = useState([]);
  const [activeTab, setActiveTab] = useState("content");
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [missingRequirements, setMissingRequirements] = useState([]);
  const [checkingRequirements, setCheckingRequirements] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  useEffect(() => {
    loadCourse();
    checkEnrollment();
    loadAllActivities();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCourse(data);
      } else if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      }
    } catch (error) {
      toast.error('Failed to load course');
    }
  };

  const checkEnrollment = async () => {
    try {
      const response = await fetch(`${API}/enrollments/my`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const enrollments = await response.json();
        const current = enrollments.find(e => e.course_id === courseId);
        setEnrollment(current);
        setIsEnrolled(!!current);
        
        // If not enrolled and not a teacher, redirect to course detail page
        if (!current && user?.role === 'student') {
          toast.error('Please enroll in this course first');
          navigate(`/courses/${courseId}`);
        }
      } else if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } else {
        setIsEnrolled(false);
        if (user?.role === 'student') {
          toast.error('Please enroll in this course first');
          navigate(`/courses/${courseId}`);
        }
      }
    } catch (error) {
      console.error('Failed to check enrollment');
      setIsEnrolled(false);
    }
  };

  const loadAllActivities = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/activities`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllActivities(data);
      }
    } catch (error) {
      console.error('Failed to load activities');
    }
  };

  const markLessonComplete = async (lessonIndex) => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/complete-lesson`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ lesson_index: lessonIndex })
      });

      if (response.ok) {
        toast.success('Lesson marked as complete!');
        checkEnrollment();
      }
    } catch (error) {
      toast.error('Failed to mark lesson complete');
    }
  };

  const handleSubmitClick = async () => {
    setCheckingRequirements(true);
    try {
      const response = await fetch(`${API}/courses/${courseId}/completion-requirements`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.can_submit) {
          // All requirements met, proceed to homework submission
          navigate(`/homework/${courseId}`);
        } else {
          // Show missing requirements modal
          setMissingRequirements(data.missing_requirements);
          setShowMissingModal(true);
        }
      } else {
        toast.error('Failed to check requirements');
      }
    } catch (error) {
      toast.error('Failed to check requirements');
    } finally {
      setCheckingRequirements(false);
    }
  };

  const handleGoToLesson = (lessonIndex, type) => {
    setCurrentLesson(lessonIndex);
    setShowMissingModal(false);
    // Set the appropriate tab based on requirement type
    if (type === 'activity') {
      setActiveTab('activities');
    } else if (type === 'discussion') {
      setActiveTab('activities'); // Activity discussions are in activities tab
    }
    toast.info(`Navigated to Lesson ${lessonIndex + 1}`);
  };

  if (!course || isEnrolled === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading...</div>
      </div>
    );
  }

  // Show enrollment required message for students who somehow got here without enrolling
  if (!isEnrolled && user?.role === 'student') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-lg rounded-2xl text-center max-w-md">
          <BookOpen className="w-16 h-16 text-[#3A5A40] mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-2xl font-bold text-[#344E41] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Enrollment Required
          </h2>
          <p className="text-[#588157] mb-6">
            You need to enroll in this course before you can access its content.
          </p>
          <Button
            onClick={() => navigate(`/courses/${courseId}`)}
            data-testid="go-to-enroll-button"
            className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 rounded-lg"
          >
            Go to Course Page
          </Button>
        </Card>
      </div>
    );
  }

  const lesson = course.content?.[currentLesson];
  const isLessonCompleted = enrollment?.completed_lessons?.includes(currentLesson);

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9] p-2 sm:p-3"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <div className="flex items-center gap-2 sm:gap-3">
            <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-base sm:text-xl font-bold text-[#344E41] line-clamp-1" style={{ fontFamily: 'Merriweather, serif' }}>{course.title}</h1>
          </div>
          {enrollment && (
            <div className="text-xs sm:text-sm text-[#588157]">
              Progress: {enrollment.progress}%
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
          <div className="lg:col-span-3 order-2 lg:order-1">
            <Card className="bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl overflow-hidden mb-4 sm:mb-6">
              {lesson?.type === 'video' && lesson?.url && (
                <div className="aspect-video bg-[#344E41]/10 flex items-center justify-center">
                  <iframe
                    src={lesson.url}
                    className="w-full h-full"
                    allowFullScreen
                    title={lesson.title}
                  />
                </div>
              )}
              {lesson?.type === 'text' && (
                <div 
                  className="p-6 sm:p-12 prose prose-sm sm:prose-lg max-w-none"
                  style={{ 
                    backgroundImage: `url('https://images.unsplash.com/photo-1676115388797-5f448ad78e44?q=85&w=800&auto=format&fit=crop')`,
                    backgroundSize: 'cover',
                    backgroundBlendMode: 'overlay',
                    backgroundColor: '#FEFAE0'
                  }}
                >
                  <div className="bg-[#FEFAE0]/95 p-4 sm:p-8 rounded-xl">
                    <h2 className="text-xl sm:text-3xl font-bold text-[#344E41] mb-4 sm:mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
                      {lesson.title || `Lesson ${currentLesson + 1}`}
                    </h2>
                    <TruncatedText content={lesson.content} maxLength={100} />
                  </div>
                </div>
              )}
              {lesson?.type === 'link' && (
                <div className="p-6 sm:p-12 text-center">
                  <h2 className="text-xl sm:text-3xl font-bold text-[#344E41] mb-4 sm:mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
                    {lesson.title || `Lesson ${currentLesson + 1}`}
                  </h2>
                  <a
                    href={lesson.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#3A5A40] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-[#344E41] duration-300 text-sm sm:text-base"
                  >
                    Open Resource
                  </a>
                </div>
              )}
            </Card>

            <div className="flex flex-wrap justify-between gap-2 mb-4 sm:mb-6">
              <Button
                disabled={currentLesson === 0}
                onClick={() => setCurrentLesson(currentLesson - 1)}
                data-testid="previous-lesson-button"
                className="bg-[#DAD7CD] text-[#3A5A40] hover:bg-[#C8C5B8] duration-200 font-medium rounded-lg text-xs sm:text-sm px-3 sm:px-4"
              >
                <span className="hidden sm:inline">Previous Lesson</span>
                <span className="sm:hidden">Prev</span>
              </Button>
              
              {user.role === 'student' && !isLessonCompleted && (
                <Button
                  onClick={() => markLessonComplete(currentLesson)}
                  data-testid="mark-complete-button"
                  className="bg-[#D4A373] text-white hover:bg-[#C89563] duration-200 font-medium rounded-lg text-xs sm:text-sm px-3 sm:px-4"
                >
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Mark Complete</span>
                  <span className="sm:hidden">Done</span>
                </Button>
              )}

              <Button
                onClick={handleSubmitClick}
                disabled={checkingRequirements}
                data-testid="submit-homework-button"
                className="bg-[#D4A373] text-white hover:bg-[#C89563] duration-200 font-medium rounded-lg text-xs sm:text-sm px-3 sm:px-4"
              >
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{checkingRequirements ? 'Checking...' : 'Submit Homework'}</span>
                <span className="sm:hidden">{checkingRequirements ? '...' : 'Submit'}</span>
              </Button>
              <Button
                disabled={currentLesson === course.content.length - 1}
                onClick={() => setCurrentLesson(currentLesson + 1)}
                data-testid="next-lesson-button"
                className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 active:scale-95 font-medium rounded-lg text-xs sm:text-sm px-3 sm:px-4"
              >
                <span className="hidden sm:inline">Next Lesson</span>
                <span className="sm:hidden">Next</span>
              </Button>
            </div>

            {/* Tabs for Activities and Discussion */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="bg-[#E9EDC9] p-1 rounded-lg">
                <TabsTrigger 
                  value="activities" 
                  data-testid="activities-tab"
                  className="data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white rounded-md px-4"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Daily Activities
                  {allActivities.filter(a => a.lesson_index === currentLesson).length > 0 && (
                    <span className="ml-2 text-xs bg-[#D4A373] text-white px-1.5 py-0.5 rounded-full">
                      {allActivities.filter(a => a.lesson_index === currentLesson).length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="discussion" 
                  data-testid="discussion-tab"
                  className="data-[state=active]:bg-[#3A5A40] data-[state=active]:text-white rounded-md px-4"
                >
                  Discussion
                </TabsTrigger>
              </TabsList>

              <TabsContent value="activities" className="mt-4">
                {allActivities.filter(a => a.lesson_index === currentLesson).length > 0 ? (
                  <ActivityViewer 
                    courseId={courseId}
                    lessonIndex={currentLesson}
                    activities={allActivities}
                    user={user}
                  />
                ) : (
                  <Card className="p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 text-center rounded-2xl">
                    <Calendar className="w-12 h-12 text-[#CCD5AE] mx-auto mb-4" />
                    <p className="text-[#588157]">No daily activities for this lesson yet.</p>
                    <p className="text-sm text-[#A3B18A] mt-2">Check back later or continue with the lesson content.</p>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="discussion" className="mt-4">
                <DiscussionForum courseId={courseId} user={user} />
              </TabsContent>
            </Tabs>
          </div>

          <div className="order-1 lg:order-2">
            <Card className="p-4 sm:p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
              <h3 className="text-lg sm:text-xl font-bold text-[#344E41] mb-3 sm:mb-4" style={{ fontFamily: 'Merriweather, serif' }}>Lessons</h3>
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                {course.content?.map((item, index) => {
                  const completed = enrollment?.completed_lessons?.includes(index);
                  return (
                    <button
                      key={index}
                      onClick={() => setCurrentLesson(index)}
                      data-testid={`lesson-${index}-button`}
                      className={`flex-shrink-0 lg:flex-shrink text-left p-2 sm:p-3 rounded-lg duration-200 flex items-center gap-2 min-w-[120px] lg:min-w-0 lg:w-full ${
                        currentLesson === index
                          ? 'bg-[#3A5A40] text-white'
                          : 'bg-[#E9EDC9] text-[#344E41] hover:bg-[#DAD7CD]'
                      }`}
                    >
                      {completed && <CheckCircle2 className="w-4 h-4 text-[#D4A373]" strokeWidth={2} />}
                      <div className="flex-1 min-w-0">
                        <span className="text-xs sm:text-sm font-medium">Lesson {index + 1}</span>
                        <p className="text-xs opacity-80 truncate">{item.title || item.type}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Missing Requirements Modal */}
      <MissingRequirementsModal
        isOpen={showMissingModal}
        onClose={() => setShowMissingModal(false)}
        requirements={missingRequirements}
        onGoToLesson={handleGoToLesson}
      />
    </div>
  );
}